import React, { useState, useContext, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Row, Col, Card, CardBody, Container } from "reactstrap";
import { Spinner } from "../../../AbstractElements";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import SharedDataContext from "../../../_helper/SharedData";
import editIcon from "../../../assets/images/tarrifManagement/edit.png";
import { Box, Typography, Button, Tabs, Tab } from "@mui/material";
import TariffModal from "../../../CommonElements/TariffModal/TariffModal";
import ServiceCityConfigForm from "../Forms/ServiceCityConfigForm";
import { getServiceCityDetails } from "../../../Services/ServiceCity.service";
import { showAlert } from "../../../Services/Shared.service";
import TollTab from "./TollTab";
import AirportTab from "./AirportTab";
import SuburbTab from "./SuburbTab";

const ServiceCityDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { allCityData, cityData, trgiggerRefresh } = useContext(SharedDataContext);

  const [isEditModal, setIsEditModal] = useState(false);
  const [fetchedCityDetails, setFetchedCityDetails] = useState(null);
  const [activeTab, setActiveTab] = useState(0);

  const cityDetails = allCityData.find((city) => String(city.id) === String(id)) || fetchedCityDetails;

  const fetchServiceCityDetails = () => {
    const obj = {
      service_city_id: id,
    };
    getServiceCityDetails(obj)
      .then((res) => {
        if (res?.data) {
          setFetchedCityDetails(res.data);
        }
      })
      .catch((err) => {
        const errorMsg = err.response?.data?.message || "Something went wrong";
        showAlert("error", errorMsg);
      });
  };

  useEffect(() => {
    if (!cityDetails && id) {
      fetchServiceCityDetails();
    }
  }, [id, cityDetails]);

  // If global context data is still loading
  if (!allCityData || allCityData.length === 0) {
    return (
      <div className="loader-box" style={{ height: "80vh" }}>
        <Spinner attrSpinner={{ className: "loader-3" }} />
      </div>
    );
  }

  if (!cityDetails) {
    return (
      <div style={{ padding: "20px" }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIosIcon />}
          sx={{
            color: "#41CC7D",
            marginBottom: 2,
            textTransform: "none",
            "&:hover": { backgroundColor: "transparent", color: "#39B54A" },
          }}
          onClick={() => navigate(-1)}
        >
          Back
        </Button>
        <Typography variant="h6" color="error">
          {"Service City details not found."}
        </Typography>
      </div>
    );
  }

  const carTypeNames = cityDetails?.carType?.map((c) => c.name).join(", ") || "-";

  const detailItems = [
    { label: "City Code", value: cityDetails?.cityCode || "-" },
    { label: "Aggregator Name", value: cityDetails?.aggregatorCompanyName || "-" },
    { label: "Company Name", value: cityDetails?.companyName || "-" },
    { label: "Place of Supply", value: cityDetails?.placeOfSupply || "-" },
    { label: "GST No", value: cityDetails?.gstNo || "-" },
    { label: "Car Types", value: carTypeNames },
    { label: "Call Centre Mobile", value: cityDetails?.callCenterMobile || "-" },
    { label: "Is Wallet Enabled", value: cityDetails?.isWalletEnabled ? "Yes" : "No" },
  ];

  return (
    <div style={{ padding: "20px" }}>
      <div style={{ display: "flex", justifyContent: "left" }}>
        <Button
          variant="text"
          startIcon={<ArrowBackIosIcon />}
          sx={{
            color: "#41CC7D",
            marginBottom: 2,
            textTransform: "none",
            "&:hover": { backgroundColor: "transparent", color: "#39B54A" },
          }}
          onClick={() => navigate("/configurations/service-city")}
        >
          Back
        </Button>
      </div>

      <Container fluid={true} className="p-0">
        <Row>
          <Col sm="12">
            <Card className="clientInfoCard">
              <CardBody
                style={{
                  backgroundColor: "rgba(20, 152, 38, 0.9)",
                  borderRadius: "10px",
                  boxShadow: `inset 0 0 8px white,inset 0 0 4px white`,
                  border: "1px solid rgba(116, 206, 106, 1)",
                  padding: "25px",
                }}
              >
                <Box sx={{ marginX: "10px" }}>
                  <Box>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: "20px" }}>
                        <Typography sx={{ fontSize: "1.67vw", color: "#ffffff", fontWeight: "600" }}>
                          {cityDetails?.name}
                        </Typography>
                        <Box
                          sx={{
                            display: "flex",
                            backgroundColor: cityDetails?.isDeleted ? "#FADBD8" : "#E3F5E1",
                            height: "2.01vw",
                            justifyContent: "space-evenly",
                            alignItems: "center",
                            borderRadius: "6px",
                            padding: "0 12px",
                            border: `2px solid ${cityDetails?.isDeleted ? "#E74C3C" : "rgba(116, 206, 106, 1)"}`,
                            gap: "8px",
                          }}
                        >
                          <Box
                            sx={{
                              width: "0.56vw",
                              height: "0.56vw",
                              background: cityDetails?.isDeleted ? "#E74C3C" : "rgba(116, 206, 106, 1)",
                              borderRadius: "50%",
                            }}
                          />
                          <Typography sx={{ fontSize: "0.97vw", fontWeight: "600", color: cityDetails?.isDeleted ? "#C0392B" : "inherit" }}>
                            {cityDetails?.isDeleted ? "Inactive" : "Active"}
                          </Typography>
                        </Box>
                      </Box>

                      <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                        <img
                          src={editIcon}
                          alt="edit"
                          style={{ width: "1.75vw", height: "1.61vw", cursor: "pointer" }}
                          onClick={() => setIsEditModal(true)}
                        />
                      </Box>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      width: "100%",
                      marginTop: "30px",
                      display: "grid",
                      gridTemplateColumns: "repeat(4, 1fr)",
                      rowGap: "30px",
                    }}
                  >
                    {detailItems.map((item, index) => (
                      <Box key={index}>
                        <Typography sx={{ fontWeight: "400", fontSize: "0.97vw", color: "white" }}>
                          {item.label}
                        </Typography>
                        <Typography sx={{ fontWeight: "600", fontSize: "0.97vw", color: "white" }}>
                          {item.value}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* Tabs Section */}
      <Box sx={{ borderBottom: 1, borderColor: "divider", marginTop: "30px" }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => setActiveTab(val)}
          sx={{
            "& .MuiTabs-flexContainer": {
              display: "flex",
            },
            "& .MuiTabs-indicator": {
              backgroundColor: "rgba(57, 181, 74, 1)",
              height: "3px",
            },
            "& .MuiTab-root.Mui-selected": {
              color: "rgba(57, 181, 74, 1)",
              fontWeight: 600,
            },
          }}
        >
          <Tab label="Toll" sx={{ textTransform: "none", fontSize: "16px" }} />
          <Tab label="Airport" sx={{ textTransform: "none", fontSize: "16px" }} />
          <Tab label="Suburb" sx={{ textTransform: "none", fontSize: "16px" }} />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <Box sx={{ paddingY: "10px" }}>
        {activeTab === 0 && <TollTab cityId={id} />}
        {activeTab === 1 && <AirportTab cityId={id} />}
        {activeTab === 2 && <SuburbTab cityId={id} />}
      </Box>

      <TariffModal
        isOpen={isEditModal}
        size="lg"
        centered
        toggler={() => setIsEditModal(false)}
        title="Edit Service City"
      >
        <ServiceCityConfigForm
          cityData={cityData}
          editId={id}
          toggleModal={() => setIsEditModal(false)}
          trgiggerRefresh={trgiggerRefresh}
        />
      </TariffModal>
    </div>
  );
};

export default ServiceCityDetailView;
